/**
 * Bootstrap: on app start, try to exchange the refresh cookie for an access token.
 * After this resolves, authStore is either 'authenticated' or 'unauthenticated'.
 *
 * Note: the refresh endpoint only returns a new access token, not the user. To hydrate
 * the user, we call /auth/me after a successful refresh.
 */
import type { AuthUser } from '@kreart/shared';
import { api } from '@/lib/api';
import { authApi } from './api';
import { authStore } from './store';

let bootstrapPromise: Promise<void> | null = null;

export function bootstrapAuth(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    try {
      const refresh = await authApi.refresh();
      if (!refresh) {
        // No session on this browser (never signed in, signed out, or expired).
        authStore.setUnauthenticated({ broadcast: false });
        return;
      }
      authStore.setAuthenticated(
        {
          id: '',
          email: '',
          fullName: '',
          role: 'VIEWER' as AuthUser['role'],
          mustChangePassword: false,
          permissions: [],
        },
        refresh.accessToken,
        refresh.accessTokenExpiresAt,
      );
      const me = await api.get<AuthUser>('/auth/me');
      authStore.setAuthenticated(me, refresh.accessToken, refresh.accessTokenExpiresAt);
    } catch {
      authStore.setUnauthenticated({ broadcast: false });
    }
  })();
  return bootstrapPromise;
}

export function resetBootstrap(): void {
  bootstrapPromise = null;
}
